import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { PropTypes } from 'prop-types';
import { useToasts } from '../../../hooks';
import { updateSite, getMarker } from '../../../utils/api/site';
import { styles } from './SiteMap.component.style';
import VamosMap from '../../VamosMap';
import VamosMarker from '../../VamosMap/VamosMarker';
import SiteInfoWindow from '../SitesInMap/SiteInfoWindow';
import CommonModal from '../../Common/Modal/Modal';
import Button from '../../Button/Button';

const SiteMap = ({
  site,
  setSiteData,
  icon,
  isSmallMap = false,
}) => {
  // Hook Toast
  const { showToast } = useToasts();
  const [currentCenter, setCurrentCenter] = useState({
    lat: parseFloat(site.latitude),
    lng: parseFloat(site.longitude),
  });

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [updates, setUpdates] = useState();
  const [shouldUpdateAddress, setShouldUpdateAddress] = useState(false);
  const [fullscreenElement, setFullScreenElement] = useState(null);

  const modalRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);

  const recenterTo = useCallback((pos) => {
    if (mapInstance) mapInstance.panTo(pos);
  }, [mapInstance]);

  const siteMarkerIcon = getMarker({
    priority: site.priority,
    status: site.status,
    icon,
  });

  const handleClose = () => {
    setIsModalVisible(false);
    setShouldUpdateAddress(false);
  };
  const updateLocation = async () => {
    updateSite(
      {
        coords_set_manually: true,
        latitude: updates.lat(),
        longitude: updates.lng(),
        should_bypass_required_custom_fields_check: true,
        should_change_marker_position: true, // signals the backend to skip Site validations (name, state, etc.) since only the marker position is being changed
      },
      site.organization_id,
      site.id,
      shouldUpdateAddress,
    )
      .then((response) => {
        if (!response.ok && response.status !== 422) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then((data) => {
        if (!data.site) {
          throw new Error(data.error || data);
        }
        setSiteData(data.site);
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      });
    handleClose();
  };

  const handleOpenUpdateLocationModal = (e) => {
    setIsModalVisible(true);
    setUpdates(e.latLng);
  };

  useEffect(() => {
    setCurrentCenter({
      lat: parseFloat(site.latitude),
      lng: parseFloat(site.longitude),
    });
  }, [site]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullScreenElement(document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const getPortalContainer = () => {
    if (fullscreenElement && mapInstance && fullscreenElement.querySelector('.gm-style')) {
      return fullscreenElement;
    }
    return document.body;
  };

  return (
    <VamosMap
      className="mb-4"
      center={currentCenter}
      mapStyle={styles.mapStyles}
      zoom={15}
      onLoad={(map) => setMapInstance(map)}
    >
      <VamosMarker
        siteId={site.id}
        lat={currentCenter.lat}
        lng={currentCenter.lng}
        icon={siteMarkerIcon}
        isDraggable
        onDragEnd={handleOpenUpdateLocationModal}
        onRecenter={recenterTo}
      >
        <SiteInfoWindow
          site={site}
          isSmallMap={isSmallMap}
        />
      </VamosMarker>
      <div ref={modalRef}>
        {isModalVisible && (
          createPortal(
            <CommonModal
              title="Are you sure you want to update the location of the pin?"
              handleClose={handleClose}
              isHeaderDivider={false}
              titleStyle="text-xl font-medium pt-7"
              className="py-14"
            >
              <div className="mb-7 mt-4">
                <input
                  type="checkbox"
                  value={shouldUpdateAddress}
                  onChange={() => setShouldUpdateAddress(!shouldUpdateAddress)}
                />
                <span className="pl-2">Also update the address</span>
              </div>
              <div className="flex space-x-4 justify-center">
                <Button
                  label="Cancel"
                  size="md"
                  color="secondary"
                  className="px-6"
                  onClick={handleClose}
                />
                <Button
                  label="Update location"
                  size="md"
                  className="px-6"
                  onClick={() => {
                    setShouldUpdateAddress(true);
                    updateLocation();
                  }}
                />
              </div>
            </CommonModal>,
            getPortalContainer(),
          )
        )}
      </div>
    </VamosMap>
  );
};

SiteMap.propTypes = {
  site: PropTypes.object.isRequired,
  setSiteData: PropTypes.func.isRequired,
  icon: PropTypes.string || PropTypes.symbol,
  isSmallMap: PropTypes.bool,
};

export default SiteMap;
